import { useEffect, useId, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getCrmCatalogSuggest, type CrmCatalogSuggestItem } from '../../../api';

type Props = {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  onPick: (item: CrmCatalogSuggestItem) => void;
};

export function OrderLineSuggestInput({
  value,
  disabled,
  placeholder,
  onChange,
  onPick,
}: Props) {
  const listId = useId();
  const [suggestions, setSuggestions] = useState<CrmCatalogSuggestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const t = window.setTimeout(() => {
      setLoading(true);
      void getCrmCatalogSuggest(q, 10)
        .then((items) => {
          setSuggestions(items);
          setOpen(items.length > 0);
        })
        .catch(() => {
          setSuggestions([]);
          setOpen(false);
        })
        .finally(() => setLoading(false));
    }, 280);
    return () => window.clearTimeout(t);
  }, [value]);

  const handleBlur = () => {
    blurTimer.current = window.setTimeout(() => setOpen(false), 160);
  };

  const handleFocus = () => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    if (suggestions.length > 0) setOpen(true);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="input-glass w-full px-2.5 py-2 rounded-lg text-xs"
        placeholder={placeholder}
        autoComplete="off"
        aria-autocomplete="list"
        aria-controls={listId}
      />
      {loading && (
        <Loader2
          size={14}
          className="absolute right-2.5 top-2.5 animate-spin"
          style={{ color: 'var(--text-3)' }}
        />
      )}
      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-xl py-1 shadow-lg"
          style={{ background: 'var(--sapphire-card)', border: '1px solid var(--border-glass)' }}
        >
          {suggestions.map((s, i) => (
            <li key={`${s.kind}-${s.product_id ?? s.system_slug ?? i}`}>
              <button
                type="button"
                role="option"
                className="press w-full text-left px-2.5 py-2 text-[11px]"
                style={{ color: 'var(--text-2)' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(s);
                  setOpen(false);
                }}
              >
                <span className="font-semibold block truncate" style={{ color: 'var(--text-1)' }}>
                  {s.label}
                </span>
                <span className="text-[10px]" style={{ color: 'var(--text-3)' }}>
                  {s.subtitle}
                  {s.unit_price > 0 ? ` · от ${Math.round(s.unit_price).toLocaleString('ru-RU')} ₽` : ''}
                  {s.kind === 'catalog_system' ? ' · конфигуратор' : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
